import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import '../../Marketplace/Marketplace.css';

const STATUS_COLORS = {
  ACTIVE:  { bg: '#d1fae5', text: '#065f46' },
  REVOKED: { bg: '#fee2e2', text: '#991b1b' },
  EXPIRED: { bg: '#f1f5f9', text: '#475569' },
};

export default function SeedKeys() {
  const { t } = useTranslation();
  const [keys, setKeys]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]       = useState({ name: '', expiresInDays: '' });
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey]   = useState(null); // one-time plaintext

  const fetchKeys = useCallback(() => {
    setLoading(true);
    axiosInstance.get('/admin/threat-seed-keys')
      .then(res => setKeys(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMsg(t('SeedKeys.failedToLoadKeys')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    setCreating(true); setMsg('');
    try {
      const body = { name: form.name || 'Threat-Hunter ingest key' };
      if (form.expiresInDays) body.expiresInDays = Number(form.expiresInDays);
      const res = await axiosInstance.post('/admin/threat-seed-keys', body);
      setNewKey(res.data); // contains plaintext "key"
      setShowCreate(false);
      setForm({ name: '', expiresInDays: '' });
      fetchKeys();
    } catch { setMsg(t('SeedKeys.failedToGenerateKey')); }
    finally { setCreating(false); }
  };

  const handleRevoke = async (id) => {
    try {
      await axiosInstance.delete(`/admin/threat-seed-keys/${id}`);
      fetchKeys();
      setMsg(t('SeedKeys.keyRevoked'));
    } catch { setMsg(t('SeedKeys.failedToRevokeKey')); }
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); setMsg(t('SeedKeys.keyCopiedToClipboard')); } catch { /* ignore */ } };

  const activeCount = keys.filter(k => k.status === 'ACTIVE').length;

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-key" /> {t('SeedKeys.heading')}</h2>
          <p className="mp-subtitle">
            {t('SeedKeys.subtitlePrefix')} <code>POST /api/public/threat-seeds</code> {t('SeedKeys.subtitleSuffix', { count: activeCount })}
          </p>
        </div>
        <button className="mp-btn-primary" onClick={() => { setShowCreate(true); setMsg(''); }}>
          <i className="fas fa-plus" /> {t('SeedKeys.generateKey')}
        </button>
      </div>

      {msg && <div className="mp-msg">{msg}</div>}

      <div className="mp-section">
        <p className="mp-subtitle" style={{ marginTop: 0 }}>
          {t('SeedKeys.introBeforeOnce')} <strong>{t('SeedKeys.once')}</strong> {t('SeedKeys.introAfterOnce')}
        </p>
        {loading ? <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('SeedKeys.loading')}</div> : (
          keys.length === 0 ? <div className="mp-empty">{t('SeedKeys.emptyState')}</div> : (
            <table className="mp-table">
              <thead><tr><th>{t('SeedKeys.colName')}</th><th>{t('SeedKeys.colKey')}</th><th>{t('SeedKeys.colStatus')}</th><th>{t('SeedKeys.colCreatedBy')}</th><th>{t('SeedKeys.colCreated')}</th><th>{t('SeedKeys.colExpires')}</th><th>{t('SeedKeys.colLastUsed')}</th><th>{t('SeedKeys.colActions')}</th></tr></thead>
              <tbody>
                {keys.map(k => {
                  const sc = STATUS_COLORS[k.status] || STATUS_COLORS.EXPIRED;
                  return (
                    <tr key={k.id}>
                      <td style={{ fontWeight: 600 }}>{k.name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{k.keyPrefix}</td>
                      <td><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.text }}>{k.status}</span></td>
                      <td style={{ fontSize: 12 }}>{k.createdBy || '—'}</td>
                      <td style={{ fontSize: 12 }}>{k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 12 }}>{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : t('SeedKeys.never')}</td>
                      <td style={{ fontSize: 12 }}>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                      <td>
                        {k.status === 'ACTIVE' && (
                          <button className="mp-btn-sm mp-btn-danger" onClick={() => handleRevoke(k.id)}>{t('SeedKeys.revoke')}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Generate modal */}
      {showCreate && (
        <div className="mp-overlay" onClick={() => setShowCreate(false)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header"><h3>{t('SeedKeys.modalGenerateTitle')}</h3>
              <button onClick={() => setShowCreate(false)}><i className="fas fa-times" /></button></div>
            <div className="mp-modal-body">
              <div className="mp-field"><label>{t('SeedKeys.nameLabel')}</label>
                <input className="mp-input" placeholder={t('SeedKeys.namePlaceholder')} value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="mp-field"><label>{t('SeedKeys.expiresLabel')}</label>
                <input type="number" className="mp-input" placeholder={t('SeedKeys.expiresPlaceholder')} value={form.expiresInDays}
                  onChange={e => setForm(f => ({ ...f, expiresInDays: e.target.value }))} /></div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowCreate(false)}>{t('SeedKeys.cancel')}</button>
              <button className="mp-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? t('SeedKeys.generating') : <><i className="fas fa-key" /> {t('SeedKeys.generate')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-time key reveal */}
      {newKey && (
        <div className="mp-overlay" onClick={() => setNewKey(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header"><h3>{t('SeedKeys.revealTitle')}</h3>
              <button onClick={() => setNewKey(null)}><i className="fas fa-times" /></button></div>
            <div className="mp-modal-body">
              <div style={{ background: '#fef6ec', border: '1px solid #e0b890', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
                {t('SeedKeys.revealBeforeOnce')} <strong>{t('SeedKeys.once')}</strong> {t('SeedKeys.revealAfterOnce')}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="mp-input" readOnly value={newKey.key}
                  style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }} onFocus={e => e.target.select()} />
                <button className="mp-btn-secondary" onClick={() => copy(newKey.key)}><i className="fas fa-copy" /> {t('SeedKeys.copy')}</button>
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-primary" onClick={() => setNewKey(null)}>{t('SeedKeys.done')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
